import type { ReactNode } from "react";
import { ArrowLink, Button } from "~/components/ui";

type ConversionSectionProps = {
  eyebrow: string;
  titleLead: string;
  titleAccent: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  /** The one line under the action («La app está en beta privada …»). */
  meta: string;
  faqLabel: string;
  /** A second reading link beside the FAQ's (T976: the diastasis page from the method's closing). */
  extraLink?: { routeKey: "diastasis"; label: string };
  /** The `waitlist` variant's panel (`WaitlistPanel`), beside the intro. */
  children?: ReactNode;
  /** The `actions` variant (Método, S4): the waitlist as a second, outline action under the
   *  self-test, instead of the panel. */
  secondary?: { label: string; href: string };
};

/**
 * T872 S3 — the closing of a public page, `waitlist` variant (October delivery
 * `HomeClosing.html`): the intro with the self-test as primary action, the beta line and
 * the link to the FAQ, beside the waitlist panel. Método mounts the `actions` variant in
 * S4 on the same section. Copy arrives as props; the primary action stays the self-test
 * and the waitlist stays secondary (the founder, 2026-10-08).
 */
export function ConversionSection({
  eyebrow,
  titleLead,
  titleAccent,
  body,
  ctaLabel,
  ctaHref,
  meta,
  faqLabel,
  extraLink,
  children,
  secondary,
}: ConversionSectionProps) {
  // The `actions` variant (Método, the delivery's `components/ConversionSection.html`):
  // the title alone on the left; the explanation, the two stacked actions, the note and
  // the FAQ link on the right. Stacked below `lg` in that same order.
  if (secondary) {
    return (
      <section className="pub-closing is-actions" id="apertura" aria-labelledby="pub-closing-title">
        <div className="pub-wrap pub-closing-split">
          <div className="pub-closing-head">
            <p className="pub-eyebrow">{eyebrow}</p>
            <h2 id="pub-closing-title">
              {titleLead}
              <br />
              <em>{titleAccent}</em>
            </h2>
          </div>
          <div className="pub-closing-copy">
            <p>{body}</p>
            <div className="pub-closing-actions">
              <Button variant="public-primary" href={ctaHref}>
                {ctaLabel}
              </Button>
              <Button variant="public-secondary" href={secondary.href}>
                {secondary.label}
              </Button>
            </div>
            <p className="pub-meta">{meta}</p>
            <ArrowLink routeKey="faq" arrow="→">
              {faqLabel}
            </ArrowLink>
            {extraLink ? (
              <ArrowLink routeKey={extraLink.routeKey} arrow="→">
                {extraLink.label}
              </ArrowLink>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pub-closing" id="apertura" aria-labelledby="pub-closing-title">
      <div className="pub-wrap pub-closing-split">
        <div className="pub-closing-intro">
          <p className="pub-eyebrow">{eyebrow}</p>
          <h2 id="pub-closing-title">
            {titleLead}
            <br />
            <em>{titleAccent}</em>
          </h2>
          <p>{body}</p>
          <Button variant="public-primary" href={ctaHref}>
            {ctaLabel}
          </Button>
          <p className="pub-meta">{meta}</p>
          <ArrowLink routeKey="faq" arrow="→">
            {faqLabel}
          </ArrowLink>
          {extraLink ? (
            <ArrowLink routeKey={extraLink.routeKey} arrow="→">
              {extraLink.label}
            </ArrowLink>
          ) : null}
        </div>
        {children}
      </div>
    </section>
  );
}
